import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  CogIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  ShieldCheckIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline'
import api from '@/services/api'

/** Type pour un paramètre de configuration */
interface Setting {
  value: string
  description: string
  isEncrypted: boolean
  updatedAt: string
}

interface SystemSettings {
  email: Record<string, Setting>
  application: Record<string, Setting>
}

interface SystemSettingsResponse {
  settings: SystemSettings
  emailServiceStatus: boolean
}

const AdminSettings: React.FC = () => {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [showPassword, setShowPassword] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [hasChanges, setHasChanges] = useState(false)

  // Fetch system settings
  const { data: settingsData, isLoading, error } = useQuery<SystemSettingsResponse>({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const response = await api.get('/system/settings')
      return response.data.data
    }
  })

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Record<string, string>) => {
      const response = await api.put('/system/settings', { settings })
      return response.data
    },
    onSuccess: () => {
      toast.success('Settings updated successfully')
      queryClient.invalidateQueries({ queryKey: ['system-settings'] })
      setHasChanges(false)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update settings')
    }
  })

  // Test email mutation
  const testEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await api.post('/system/settings/email/test', { testEmail: email })
      return response.data
    },
    onSuccess: () => {
      toast.success('Test email sent successfully')
      setTestEmail('')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to send test email')
    }
  })

  // Initialize form data when settings are loaded
  useEffect(() => {
    if (settingsData) {
      const initialData: Record<string, string> = {}
      Object.values(settingsData.settings).forEach((settings) => {
        Object.entries(settings as Record<string, Setting>).forEach(([key, setting]) => {
          initialData[key] = setting.value
        })
      })
      setFormData(initialData)
    }
  }, [settingsData])

  // Handle form field changes
  const handleFieldChange = (key: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [key]: value }
      // Check if there are changes compared to original
      const hasChanges = Object.entries(newData).some(([k, v]) => {
        const originalValue = getOriginalValue(k)
        return originalValue !== v
      })
      setHasChanges(hasChanges)
      return newData
    })
  }

  // Get original value for comparison
  const getOriginalValue = (key: string): string => {
    if (!settingsData) return ''
    for (const settings of Object.values(settingsData.settings)) {
      if (settings[key]) {
        return settings[key].value
      }
    }
    return ''
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!hasChanges) return

    const changedSettings: Record<string, string> = {}
    Object.entries(formData).forEach(([key, value]) => {
      const originalValue = getOriginalValue(key)
      if (originalValue !== value) {
        changedSettings[key] = value
      }
    })

    updateSettingsMutation.mutate(changedSettings)
  }

  // Handle test email
  const handleTestEmail = (e: React.FormEvent) => {
    e.preventDefault()
    if (!testEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }
    testEmailMutation.mutate(testEmail)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <XCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-xs text-gray-600 dark:text-gray-400">Failed to load system settings</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
            <CogIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">System Settings</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Configure email and application settings for your organization
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Status</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm flex items-center gap-1">
              {settingsData?.emailServiceStatus ? (
                <>
                  <CheckCircleIcon className="h-3 w-3 text-emerald-500" />
                  Operational
                </>
              ) : (
                <>
                  <XCircleIcon className="h-3 w-3 text-red-500" />
                  Attention Needed
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <EnvelopeIcon className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Email Configuration</h2>
                <p className="text-gray-600 dark:text-gray-300 text-xs">
                  Configure SMTP settings for email notifications
                </p>
              </div>
            </div>
            <div className="flex items-center">
              {settingsData?.emailServiceStatus ? (
                <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400">
                  <CheckCircleIcon className="h-3 w-3" />
                  Connected
                </div>
              ) : (
                <div className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                  <XCircleIcon className="h-3 w-3" />
                  Not Connected
                </div>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          <div className="space-y-4">
            {settingsData?.settings.email &&
              Object.entries(settingsData.settings.email).map(([key, setting]) => (
                <div key={key} className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                  <div className="lg:col-span-1">
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      {setting.isEncrypted && (
                        <ShieldCheckIcon className="h-3 w-3 text-blue-500 inline ml-1" />
                      )}
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{setting.description}</p>
                  </div>
                  <div className="lg:col-span-2">
                    {key === 'smtp_pass' ? (
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={formData[key] || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          className="block w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200 pr-10"
                          placeholder="Enter SMTP password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-3 w-3" />
                          ) : (
                            <EyeIcon className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    ) : key === 'smtp_port' ? (
                      <input
                        type="number"
                        value={formData[key] || ''}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        className="block w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                        placeholder="587"
                        min="1"
                        max="65535"
                      />
                    ) : key === 'smtp_secure' ? (
                      <select
                        value={formData[key] || 'false'}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        className="block w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                      >
                        <option value="false">No (STARTTLS)</option>
                        <option value="true">Yes (SSL/TLS)</option>
                      </select>
                    ) : (
                      <input
                        type={key.includes('email') ? 'email' : 'text'}
                        value={formData[key] || ''}
                        onChange={(e) => handleFieldChange(key, e.target.value)}
                        className="block w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                        placeholder={`Enter ${setting.description.toLowerCase()}`}
                      />
                    )}
                  </div>
                </div>
              ))}

            {!settingsData?.emailServiceStatus && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-amber-800 dark:text-amber-300">
                    <p className="font-medium mb-1">Email service is not configured</p>
                    <p>
                      Users will not receive email notifications until the email configuration is properly set up and tested.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={!hasChanges || updateSettingsMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex justify-center items-center gap-2 py-2 px-3 transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateSettingsMutation.isPending ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Email Settings'
              )}
            </button>

            {hasChanges && (
              <button
                type="button"
                onClick={() => {
                  if (settingsData) {
                    const initialData: Record<string, string> = {}
                    Object.values(settingsData.settings).forEach((settings) => {
                      Object.entries(settings as Record<string, Setting>).forEach(([key, setting]) => {
                        initialData[key] = setting.value
                      })
                    })
                    setFormData(initialData)
                    setHasChanges(false)
                  }
                }}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex justify-center items-center py-2 px-3 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200"
              >
                Discard Changes
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Test Email */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <EnvelopeIcon className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Test Email Configuration</h2>
              <p className="text-gray-600 dark:text-gray-300 text-xs">
                Send a test email to verify your email configuration is working correctly
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleTestEmail} className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                Test Email Address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="Enter test email address"
                className="block w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                required
              />
            </div>
            <button
              type="submit"
              disabled={testEmailMutation.isPending || !testEmail.trim()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex justify-center items-center gap-2 py-2 px-3 transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {testEmailMutation.isPending ? (
                <>
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Test Email'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Application Settings */}
      {settingsData?.settings.application && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                <BuildingLibraryIcon className="h-4 w-4" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Application Settings</h2>
                <p className="text-gray-600 dark:text-gray-300 text-xs">
                  General application configuration and preferences
                </p>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="space-y-4">
              {Object.entries(settingsData.settings.application).map(([key, setting]) => (
                <div key={key} className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                  <div className="lg:col-span-1">
                    <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400">{setting.description}</p>
                  </div>
                  <div className="lg:col-span-2">
                    <input
                      type={key.includes('url') ? 'url' : 'text'}
                      value={formData[key] || ''}
                      onChange={(e) => handleFieldChange(key, e.target.value)}
                      className="block w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
                      placeholder={`Enter ${setting.description.toLowerCase()}`}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!hasChanges || updateSettingsMutation.isPending}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex justify-center items-center gap-2 py-2 px-3 transition-all duration-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateSettingsMutation.isPending ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Application Settings'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminSettings